import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import client from '../utils/fetchClient';
import { resolvePermissions } from '../permissions';

/**
 * Real session auth. Backed by /api/auth/* (HMAC session cookie). Replaces the
 * mocked parts of OrgContext / UserContext, which are now thin shims over this.
 */
const AuthContext = createContext(undefined);

const EMPTY_PERMS = resolvePermissions(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    authenticated: false,
    user: null,
    org: null,
    membership: null,
    role: null,
    permissions: EMPTY_PERMS,
  });

  const applyBundle = useCallback((b) => {
    if (b && b.authenticated) {
      setState({
        loading: false,
        authenticated: true,
        user: b.user,
        org: b.org,
        membership: b.membership,
        role: b.role,
        permissions: b.permissions || resolvePermissions(b.role),
      });
    } else {
      setState({
        loading: false,
        authenticated: false,
        user: null,
        org: null,
        membership: null,
        role: null,
        permissions: EMPTY_PERMS,
      });
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await client.get('/api/auth/me');
      const data = res.ok ? await res.json() : { authenticated: false };
      applyBundle(data);
      return data;
    } catch {
      applyBundle({ authenticated: false });
      return { authenticated: false };
    }
  }, [applyBundle]);

  const login = useCallback(
    async (email, password) => {
      const res = await client.post('/api/auth/login', { body: { email, password } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Sign in failed');
      }
      applyBundle(data);
      return data;
    },
    [applyBundle],
  );

  const logout = useCallback(async () => {
    try {
      await client.post('/api/auth/logout');
    } catch {
      /* ignore */
    }
    applyBundle({ authenticated: false });
  }, [applyBundle]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      ...state,
      isOwner: state.role === 'owner',
      isAdmin: state.role === 'admin',
      isDeveloper: state.role === 'developer',
      can: (gate) => !!state.permissions[gate],
      login,
      logout,
      refresh,
    }),
    [state, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
